import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '../../components/PageTransition';
import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const isMounted = useRef(true);
  
  const [isSignUp, setIsSignUp] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    orgName: ''
  });

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (!loading && user) {
      navigate('/projects', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (authLoading) return;
    setAuthLoading(true);
    setError(null);

    try {
      if (isSignUp) {
        await authService.signUp(formData.email, formData.password, formData.fullName, formData.orgName);
        if (isMounted.current) navigate('/billing', { replace: true });
      } else {
        await authService.login(formData.email, formData.password);
        if (isMounted.current) setAuthLoading(false);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err.message || "An unexpected error occurred");
        setAuthLoading(false);
      }
    }
  };

  if (loading && !authLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-rutabaga-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rutabaga-pink"></div>
      </div>
    );
  }

  return (
    <PageTransition className="flex items-center justify-center bg-rutabaga-bg min-h-screen p-4 font-sans">
      <div className="w-full max-w-[440px] p-10 bg-white rounded-lg shadow-xl shadow-black/5 border border-gray-100">
        <div className="flex flex-col items-center mb-10 text-center">
          <div className="w-20 h-20 bg-rutabaga-pink rounded-md flex items-center justify-center text-white font-black text-5xl mb-6 shadow-2xl shadow-rutabaga-pink/30">
            R
          </div>
          <h1 className="text-3xl font-black text-rutabaga-dark tracking-tight">
            {isSignUp ? 'Create Lab Account' : 'Welcome to Rutabaga'}
          </h1>
          <p className="text-gray-500 mt-2 font-medium">
            {isSignUp ? 'Set up your researcher workspace.' : 'Sign in to access your projects.'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 text-sm rounded-2xl border border-red-100 font-bold text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {isSignUp && (
            <>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Full Name</label>
                <input
                  type="text"
                  required
                  className="rutabaga-input"
                  placeholder="Jane Doe"
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Organization Name</label>
                <input
                  type="text"
                  required
                  className="rutabaga-input"
                  placeholder="Acme Research"
                  value={formData.orgName}
                  onChange={(e) => setFormData({...formData, orgName: e.target.value})}
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Email Address</label>
            <input
              type="email"
              required
              className="rutabaga-input"
              placeholder="researcher@rutabaga.app"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Password</label>
            <input
              type="password"
              required
              className="rutabaga-input"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
            />
          </div>

          <button
            type="submit"
            disabled={authLoading}
            className="w-full rutabaga-button-primary mt-6 text-lg py-4"
          >
            {authLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Processing...</span>
              </div>
            ) : isSignUp ? 'Start Free Trial' : 'Sign In'}
          </button>
        </form>

        <div className="mt-10 pt-6 border-t border-gray-100 text-center">
          <button
            onClick={() => { setIsSignUp(!isSignUp); setError(null); }}
            className="text-sm font-black text-rutabaga-pink hover:text-rutabaga-pink-hover transition-colors"
          >
            {isSignUp ? (
              <>Already have an account? <span className="underline decoration-2 underline-offset-4">Sign in</span></>
            ) : (
              <>New to Rutabaga? <span className="underline decoration-2 underline-offset-4">Create an account</span></>
            )}
          </button>
        </div>
      </div>
    </PageTransition>
  );
};

export default Login;